"use client";

import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  FC,
  MouseEvent,
  MouseEventHandler,
  ReactNode,
  Ref,
} from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses } from "@huub-web/utils";

import type { BorderProp, ChildrenProp } from "../../props";
import type { IconProp } from "../../props/IconProp";
import { Icon } from "../Icon/Icon";
import { Tooltip } from "../Tooltip/Tooltip";
import { Badge } from "../Badge/Badge";
import type { IconName } from "../../theme";
import { Text } from "../Text/Text";

import { borders } from "../../theme/css/borders.css";
import { focusVisibleInset } from "../../theme/css/focusVisible.css";
import {
  itemStyle,
  containerStyle,
  badgeStyle,
} from "./FixedNavigationItem.css";

export type FixedNavigationItemProps = ButtonHTMLAttributes<HTMLButtonElement> &
  AnchorHTMLAttributes<HTMLAnchorElement> &
  IconProp &
  BorderProp &
  ChildrenProp &
  RecipeVariants<typeof containerStyle> &
  RecipeVariants<typeof itemStyle> & {
    badge?: ReactNode;
    tooltip?: ReactNode;
    disabled?: boolean;
    progress?: number; // 0-100
    activeIcon?: IconName;
    rounded?: boolean;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * A fixed navigation item.
 */
export const FixedNavigationItem: FC<FixedNavigationItemProps> = ({
  icon = "document",
  activeIcon,
  badge,
  href,
  tooltip,
  className,
  size,
  appearance,
  variant,
  progress,
  active = false,
  disabled = false,
  border,
  children,
  onClick,
  rounded,
  ref,
  ...props
}) => {
  const classNames = mergeClasses(
    itemStyle({ appearance, variant, active }),
    focusVisibleInset,
    border ? borders[border] : undefined,
    className,
  );

  const itemProps = {
    progress,
    icon,
    badge,
    active,
    activeIcon,
    variant,
    tooltip,
    rounded,
  };

  const handleClick: MouseEventHandler<
    HTMLAnchorElement | HTMLButtonElement
  > = (e) => {
    if (!disabled) {
      onClick?.(e as MouseEvent<HTMLButtonElement>);
    }
    e?.preventDefault();
  };

  return (
    <div ref={ref} className={containerStyle({ size, variant, active })}>
      <Tooltip
        value={variant === "full" ? undefined : tooltip}
        placement="right"
        focus
      >
        {href ? (
          <a
            href={href}
            className={classNames}
            onClick={handleClick}
            {...(disabled ? { "aria-disabled": true } : {})}
            {...props}
          >
            {children ? children : <Content {...itemProps} />}
          </a>
        ) : (
          <button
            className={classNames}
            disabled={disabled}
            onClick={handleClick}
            {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
          >
            {children ? children : <Content {...itemProps} />}
          </button>
        )}
      </Tooltip>
    </div>
  );
};

const Content: FC<
  Pick<
    FixedNavigationItemProps,
    | "badge"
    | "icon"
    | "activeIcon"
    | "progress"
    | "active"
    | "variant"
    | "tooltip"
  >
> = ({ badge, icon, activeIcon, active, variant, tooltip }) => (
  <>
    <Icon
      icon={
        active
          ? activeIcon
            ? activeIcon
            : (`${icon}Filled` as IconName)
          : icon
      }
    />
    {/* {variant === "full" ? null : (
      <AnimatePresence>
        {progress !== undefined && progress !== 0 && active && (
          <CircularProgress
            className={progressStyle}
            progress={progress}
            variant="opaque"
          />
        )}
      </AnimatePresence>
    )} */}
    {variant === "full" && <Text>{tooltip}</Text>}
    {badge !== undefined && (
      <Badge value={badge} className={badgeStyle} variant="secondary" />
    )}
  </>
);
